/**
 * ClaimsListPage (CC-Claims) — codex ClaimsPage.tsx rebuilt on the house list
 * stack, split per-page (list / create / detail).
 *
 * useClaimsTable (useAntdTable + pre-first-fetch guard) + claimStore filters +
 * useListPage state triad + GenericFilterPopover (side / status / type). The
 * claim's required soLineId/poLineId link surfaces as the 銷售索賠 / 採購索賠
 * side tag (dual placement) and the line/product cell; the whole row drills to
 * the detail page, where the LinkedDocumentRail reaches the SO/PO line and the
 * OCC 訂單異常 lens. Behind the `sales.claims` flag (gated in App.tsx).
 *
 * NOTE: zero spec exists for claims — this surface is a styled placeholder
 * pending 林芊's spec-or-deschedule call (a quiet notice states that in-page).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Dropdown, Input, Table, Tag, theme } from 'antd';
import type { TableProps } from 'antd';
import {
  EyeOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import GenericFilterPopover from '@/components/common/GenericFilterPopover';
import type { FieldConfig, FilterValues } from '@/components/common/GenericFilterPopover';
import { HighlightText } from '@/components/common/HighlightText';
import { useListPage } from '@/hooks/useListPage';
import type { TableHookResult } from '@/hooks/useListPage';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { CLAIM_SIDES, CLAIM_STATUSES, CLAIM_TYPES } from '@/types/api/claim';
import type { Claim, ClaimSide, ClaimStatus, ClaimType } from '@/types/api/claim';
import { useClaimStore } from './claimStore';
import type {
  ClaimFilters,
  ClaimSideFilter,
  ClaimStatusFilter,
  ClaimTypeFilter,
} from './claimStore';
import { useClaimsTable } from './useClaims';
import { CLAIM_SIDE_TAG_COLOR, CLAIM_STATUS_TAG_COLOR } from './claimConstants';

/** Popover-local filter value shape for the claims list. */
interface ClaimFilterPopoverValues extends FilterValues {
  side?: string;
  status?: string;
  type?: string;
}

const ClaimsListPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['claims', 'common']);
  const { token } = theme.useToken();

  const { filters, setSearch, setSide, setStatus, setType, resetFilters } = useClaimStore();
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  // Badge parity: single-choice fields count when off 'all'.
  const customFilterCounters = useMemo(
    () => ({
      side: (value: unknown) => (value === 'all' ? 0 : 1),
      status: (value: unknown) => (value === 'all' ? 0 : 1),
      type: (value: unknown) => (value === 'all' ? 0 : 1),
    }),
    []
  );

  const { tableProps, isLoading, refresh, activeFilterCount, isFiltered, listState } = useListPage<
    ClaimFilters,
    Claim
  >({
    useTableHook: useClaimsTable as (filters: ClaimFilters) => TableHookResult<Claim>,
    filters,
    filterCountExclusions: ['search'],
    customFilterCounters,
  });

  const filterFields: FieldConfig[] = useMemo(
    () => [
      {
        key: 'side',
        label: t('claims:filters.side'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('claims:filters.allSides') },
          ...CLAIM_SIDES.map((side) => ({ value: side, label: t(`claims:sides.${side}`) })),
        ],
        resetText: t('common:actions.reset'),
      },
      {
        key: 'status',
        label: t('claims:filters.status'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('claims:filters.allStatuses') },
          ...CLAIM_STATUSES.map((status) => ({
            value: status,
            label: t(`claims:statuses.${status}`),
          })),
        ],
        resetText: t('common:actions.reset'),
      },
      {
        key: 'type',
        label: t('claims:filters.type'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('claims:filters.allTypes') },
          ...CLAIM_TYPES.map((type) => ({ value: type, label: t(`claims:types.${type}`) })),
        ],
        resetText: t('common:actions.reset'),
      },
    ],
    [t]
  );

  const initialPopoverFilters: ClaimFilterPopoverValues = useMemo(
    () => ({ side: filters.side, status: filters.status, type: filters.type }),
    [filters.side, filters.status, filters.type]
  );

  const handleApplyFilters = (values: ClaimFilterPopoverValues) => {
    setSide((values.side as ClaimSideFilter) || 'all');
    setStatus((values.status as ClaimStatusFilter) || 'all');
    setType((values.type as ClaimTypeFilter) || 'all');
  };

  const columns: TableProps<Claim>['columns'] = [
    {
      title: t('claims:fields.claimNumber'),
      dataIndex: 'claimNumber',
      key: 'claimNumber',
      width: 190,
      render: (_: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>
            <HighlightText text={record.claimNumber} searchTerm={filters.search} fallback="-" />
          </span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            <HighlightText
              text={record.reference.orderNumber}
              searchTerm={filters.search}
              fallback=""
            />
          </span>
        </div>
      ),
    },
    {
      title: t('claims:fields.side'),
      dataIndex: 'side',
      key: 'side',
      width: 120,
      render: (side: ClaimSide) => (
        <Tag color={CLAIM_SIDE_TAG_COLOR[side]} style={{ margin: 0 }}>
          {t(`claims:sides.${side}`)}
        </Tag>
      ),
    },
    {
      title: t('claims:fields.lineProduct'),
      key: 'lineProduct',
      width: 240,
      render: (_: unknown, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span>
            <HighlightText
              text={record.reference.productName}
              searchTerm={filters.search}
              fallback="-"
            />
          </span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {record.reference.lineId} · {record.reference.productId}
          </span>
        </div>
      ),
    },
    {
      title: t('claims:fields.type'),
      dataIndex: 'type',
      key: 'type',
      width: 110,
      render: (type: ClaimType) => t(`claims:types.${type}`),
    },
    {
      title: t('claims:fields.impact'),
      key: 'impact',
      width: 150,
      align: 'right',
      render: (_: unknown, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5 tw-items-end">
          {record.quantity !== undefined && (
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {t('claims:fields.quantity')}: {record.quantity}
            </span>
          )}
          {record.amount !== undefined && (
            <span style={{ fontVariantNumeric: 'tabular-nums', color: token.colorTextSecondary }}>
              {record.currency} {record.amount.toLocaleString()}
            </span>
          )}
          {record.quantity === undefined && record.amount === undefined && renderDash()}
        </div>
      ),
    },
    {
      title: t('claims:fields.status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: ClaimStatus) => (
        <Tag color={CLAIM_STATUS_TAG_COLOR[status]} style={{ margin: 0 }}>
          {t(`claims:statuses.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('claims:fields.owner'),
      dataIndex: 'owner',
      key: 'owner',
      width: 150,
      render: (value: string) => value || renderDash(),
    },
    {
      title: t('claims:fields.actions'),
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'view',
                label: t('claims:actions.view'),
                icon: <EyeOutlined />,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  navigate(`/auth/claims/${record.id}`);
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            aria-label={t('claims:actions.view')}
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="tw-px-6 tw-pb-8">
      <div className="tw-mb-6">
        <h1 style={{ fontSize: token.fontSizeHeading2, fontWeight: token.fontWeightStrong }}>
          {t('claims:list.title')}
        </h1>
      </div>

      {/* Spec-pending notice — this surface is a styled placeholder. */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: token.marginLG }}
        message={t('claims:placeholderNotice')}
      />

      {/* Search + filter + create toolbar */}
      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
        <Input
          placeholder={t('claims:list.searchPlaceholder')}
          value={filters.search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          size="large"
          suffix={<SearchOutlined style={{ fontSize: token.sizeMD, color: token.colorPrimary }} />}
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        />
        <GenericFilterPopover<ClaimFilterPopoverValues>
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          onApply={handleApplyFilters}
          initialFilters={initialPopoverFilters}
          fields={filterFields}
          applyText={t('common:actions.apply')}
          cancelText={t('common:actions.cancel')}
        >
          <Badge count={activeFilterCount}>
            <Button
              aria-label={t('common:actions.filter')}
              icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
              size="large"
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                width: `${controlHeightTokens.controlHeightXL}px`,
              }}
            />
          </Badge>
        </GenericFilterPopover>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => navigate('/auth/claims/add')}
          style={{
            marginLeft: 'auto',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
            paddingLeft: token.paddingLG,
            paddingRight: token.paddingLG,
          }}
        >
          {t('claims:list.addClaim')}
        </Button>
      </div>

      {/* Table — S0.3 state triad via TableContainer + ListEmptyState */}
      <TableContainer
        errorState={{
          active: listState === 'error',
          title: t('claims:states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<Claim>
          {...tableProps}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => navigate(`/auth/claims/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: isFiltered ? (
              <ListEmptyState
                title={t('claims:states.noResults')}
                actionLabel={t('claims:states.clearFilters')}
                onAction={resetFilters}
                actionType="default"
              />
            ) : (
              <ListEmptyState
                title={t('claims:states.empty')}
                actionLabel={t('claims:states.createFirst')}
                onAction={() => navigate('/auth/claims/add')}
                actionIcon={<PlusOutlined />}
              />
            ),
          }}
          pagination={{ ...tableProps.pagination, showSizeChanger: false }}
          scroll={{ x: 1080 }}
        />
      </TableContainer>
    </div>
  );
};

export default ClaimsListPage;
